import { FitbitSummary } from '../../../services/fitbitCollectionDataService';
import { calendarDaySpan, DateRange, validRange } from '../../../utils/dateRanges';
export { validRange } from '../../../utils/dateRanges';
export type { DateRange } from '../../../utils/dateRanges';

const dayMs = 86400000;
export type AvailableMonth = DateRange & { month: string; days: number; records: number };
export type MetricAvailability = {
  routeKey: string; records: number; recordedDays: number; periodDays: number;
  firstDate: string | null; lastDate: string | null; months: AvailableMonth[];
};

export function datesInRange(range: DateRange) {
  if (!validRange(range)) return [];
  return Array.from({ length: Math.round((Date.parse(range.endDate) - Date.parse(range.startDate)) / dayMs) + 1 },
    (_, index) => new Date(Date.parse(range.startDate) + index * dayMs).toISOString().slice(0, 10));
}

export function collectionHistoryRange(summary: FitbitSummary, now = new Date()): DateRange | null {
  const starts = summary.campaigns.map(campaign => campaign.historyStart).filter(date => /^\d{4}-\d{2}-\d{2}$/.test(date)).sort();
  if (!starts.length) return null;
  return { startDate: starts[0], endDate: now.toISOString().slice(0, 10) };
}

export function summarizeAvailability(summary: FitbitSummary, range: DateRange): MetricAvailability[] {
  // Availability can combine multiple bounded historical reads. Count that
  // entire span without allocating chart dates or applying one-request limits.
  const periodDays = calendarDaySpan(range) + 1;
  const byRoute = new Map<string, Map<string, number>>();
  for (const row of summary.dailyCoverage || []) {
    if (row.date < range.startDate || row.date > range.endDate || Number(row.presentRecords) <= 0) continue;
    const days = byRoute.get(row.routeKey) || new Map<string, number>();
    days.set(row.date, (days.get(row.date) || 0) + Number(row.presentRecords));
    byRoute.set(row.routeKey, days);
  }
  const counts = new Map(summary.metrics.map(metric => [metric.routeKey, metric]));
  return summary.catalog.map(metric => {
    const days = byRoute.get(metric.routeKey) || new Map<string, number>();
    const months = new Map<string, AvailableMonth>();
    for (const [date, records] of Array.from(days)) {
      const month = date.slice(0, 7);
      const endOfMonth = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).toISOString().slice(0, 10);
      const row = months.get(month) || { month, days: 0, records: 0,
        startDate: `${month}-01` < range.startDate ? range.startDate : `${month}-01`,
        endDate: endOfMonth > range.endDate ? range.endDate : endOfMonth };
      row.days++; row.records += records; months.set(month, row);
    }
    const count = counts.get(metric.routeKey);
    return { routeKey: metric.routeKey, records: Number(count?.presentRecords || 0), recordedDays: days.size, periodDays,
      firstDate: count?.firstRecord?.slice(0, 10) || null, lastDate: count?.lastRecord?.slice(0, 10) || null,
      months: Array.from(months.values()).sort((a, b) => b.days - a.days || b.records - a.records || a.month.localeCompare(b.month)) };
  });
}

export const monthLabel = (month: string) => new Date(`${month}-01T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
